import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, money, apiErr } from "@/lib/api";
import { StatCard, Button, Badge, Empty, Field, Input } from "@/lib/ui";
import { ScreenshotButton, downloadBlob } from "@/pages/admin/referorShared";

export default function ReferorEarnings() {
  const [d, setD] = useState(null);
  const [rows, setRows] = useState([]);
  const [range, setRange] = useState({ start: "", end: "" });
  const load = () => {
    api.get("/referor/dashboard").then(({ data }) => setD(data));
    api.get("/referor/ledger", { params: { start: range.start || undefined, end: range.end || undefined } }).then(({ data }) => setRows(data));
  };
  useEffect(load, [range.start, range.end]); // eslint-disable-line react-hooks/exhaustive-deps
  const pdf = async () => {
    try { await downloadBlob("/referor/ledger/pdf", { start: range.start || undefined, end: range.end || undefined }, "my-payment-history.pdf"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!d) return <p className="text-slate-400">Loading…</p>;
  const s = d.summary;
  const payments = rows.filter((r) => r.type === "payment");
  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl font-extrabold text-slate-900">My Earnings & Payment History</h1>
          <p className="mt-1 text-sm text-slate-500">Payouts on the <b>25th of every month</b> · Next: {new Date(d.next_payment_date).toLocaleDateString("en-IN", { day: "numeric", month: "long" })}</p>
        </div>
        <Button onClick={pdf} data-testid="referor-pdf-btn"><i className="fa-solid fa-file-pdf" /> Download My Payment History (PDF)</Button>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard testid="earn-total" label="Total Commissions" value={money(s.earned)} icon="fa-coins" tone="green" />
        <StatCard testid="earn-paid" label="Payments Received" value={money(s.paid)} icon="fa-money-bill-wave" tone="blue" />
        <StatCard testid="earn-balance" label="Outstanding Balance" value={money(s.balance)} icon="fa-wallet" tone="orange" />
        <StatCard testid="earn-count" label="Completed Payments" value={s.payments} icon="fa-circle-check" />
      </div>
      <div className="mt-5 flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200 bg-white p-4">
        <Field label="From"><Input type="date" data-testid="earn-start" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} /></Field>
        <Field label="To"><Input type="date" data-testid="earn-end" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} /></Field>
        {(range.start || range.end) && <Button variant="outline" onClick={() => setRange({ start: "", end: "" })}>Clear</Button>}
        <p className="ml-auto text-xs text-slate-500">{payments.length} payment{payments.length === 1 ? "" : "s"} · {rows.length} transactions{(range.start || range.end) ? " in period" : ""}</p>
      </div>
      <h2 className="mt-6 font-head text-lg font-bold text-slate-900">Payment History</h2>
      {payments.length === 0 ? <Empty text="No payments received yet" icon="fa-money-bill-wave" /> : (
        <div className="mt-3 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead><tr className="text-xs uppercase text-slate-400"><th className="px-3 py-2">Txn ID</th><th className="px-3 py-2">Date</th><th className="px-3 py-2">Amount</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Previous</th><th className="px-3 py-2">Remaining</th><th className="px-3 py-2">Screenshot</th></tr></thead>
            <tbody>{payments.map((t) => (
              <tr key={t.txn_id} className="border-t border-slate-100" data-testid={`pay-row-${t.txn_id}`}>
                <td className="px-3 py-2 font-mono text-xs">{t.txn_id}</td>
                <td className="px-3 py-2">{t.payment_date || t.created_at.slice(0, 10)}</td>
                <td className="px-3 py-2 font-bold text-slate-900">{money(t.debit)}</td>
                <td className="px-3 py-2"><Badge tone={t.status === "paid" ? "emeraldSoft" : t.status === "reversed" ? "red" : "slate"}>{t.status}</Badge></td>
                <td className="px-3 py-2">{money(t.prev_balance)}</td>
                <td className="px-3 py-2">{money(t.new_balance)}</td>
                <td className="px-3 py-2">{t.has_screenshot ? <ScreenshotButton txnId={t.txn_id} /> : "—"}</td>
              </tr>))}</tbody>
          </table>
        </div>
      )}
      <h2 className="mt-8 font-head text-lg font-bold text-slate-900">Full Ledger (Credits & Debits)</h2>
      {rows.length === 0 ? <Empty text="No transactions yet" /> : (
        <div className="mt-3 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead><tr className="text-xs uppercase text-slate-400"><th className="px-3 py-2">Txn ID</th><th className="px-3 py-2">Date</th><th className="px-3 py-2">Type</th><th className="px-3 py-2 text-right">Credit</th><th className="px-3 py-2 text-right">Debit</th><th className="px-3 py-2 text-right">Balance</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Notes</th></tr></thead>
            <tbody>{rows.map((t) => (
              <tr key={t.txn_id} className="border-t border-slate-100" data-testid={`ledger-row-${t.txn_id}`}>
                <td className="px-3 py-2 font-mono text-xs">{t.txn_id}</td>
                <td className="px-3 py-2 text-xs">{new Date(t.created_at).toLocaleString()}</td>
                <td className="px-3 py-2 capitalize">{t.type}</td>
                <td className="px-3 py-2 text-right text-emerald-700">{t.credit ? money(t.credit) : ""}</td>
                <td className="px-3 py-2 text-right text-red-600">{t.debit ? money(t.debit) : ""}</td>
                <td className="px-3 py-2 text-right font-semibold">{money(t.new_balance)}</td>
                <td className="px-3 py-2"><Badge tone={t.status === "reversed" ? "red" : "slate"}>{t.status}</Badge></td>
                <td className="px-3 py-2 text-xs text-slate-500">{t.notes}</td>
              </tr>))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
